import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Link, useNavigate } from "../i18n/router";
import { Calendar, MapPin, Users, Heart, Sparkles, ArrowRight, CheckCircle, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const { t, localePath } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Real upcoming events for the "Ближайшая неделя" teaser. Pre-launch the DB
  // is empty, so we render the section only when there's at least one real
  // event — never hardcoded demo content.
  const [upcomingPreview, setUpcomingPreview] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/events`);
        if (!res.ok) return;
        const events = await res.json();
        if (cancelled) return;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = (events || [])
          .filter((e) => {
            try { return new Date(e.date) >= today; } catch { return false; }
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 2);
        setUpcomingPreview(upcoming);
      } catch (_e) {
        // Silent — the section just won't render if the request fails.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Show the post-magic-link welcome banner ONLY if the URL flag is set AND we're
  // authenticated. After 6s or on dismiss we strip the flag so a refresh shows
  // the normal logged-in home.
  const isWelcome = !!user && searchParams.get("welcome") === "1";
  const dismissWelcome = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("welcome");
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    if (!isWelcome) return;
    const t = setTimeout(dismissWelcome, 8000);
    return () => clearTimeout(t);
  }, [isWelcome]);

  return (
    <div className="min-h-screen">
      {/* Post-magic-link welcome banner — only shown immediately after login */}
      {isWelcome && (
        <div
          className="max-w-4xl mx-auto px-6 pt-8"
          data-testid="welcome-banner"
        >
          <div
            className="eunoia-surface relative px-6 py-5 flex items-start gap-4"
            style={{ borderLeft: "4px solid var(--eunoia-accent)" }}
          >
            <CheckCircle className="h-6 w-6 flex-shrink-0 mt-0.5" style={{ color: "var(--eunoia-accent)" }} />
            <div className="flex-1 min-w-0">
              <h2 className="eunoia-title-small mb-1">
                Добро пожаловать{user?.displayName ? `, ${user.displayName.split(' ')[0]}` : ""} в EUNOIA
              </h2>
              <p className="eunoia-body text-sm" style={{ color: "var(--eunoia-soft)" }}>
                Ваш вход успешно подтверждён. Добро пожаловать в сообщество EUNOIA.
              </p>
            </div>
            <button
              onClick={dismissWelcome}
              data-testid="dismiss-welcome-btn"
              className="flex-shrink-0 p-1 hover:bg-black/[0.04] rounded-full transition-colors"
              aria-label="Закрыть"
            >
              <X className="h-4 w-4" style={{ color: "var(--eunoia-soft)" }} />
            </button>
          </div>
        </div>
      )}

      {/* Hero Section - Sacred Space */}
      <section className="relative overflow-hidden">
        {/* Ambient Light */}
        <div aria-hidden="true" className="absolute top-[-100px] left-[10%] eunoia-glow-primary eunoia-glow-animate"></div>
        <div aria-hidden="true" className="absolute top-[200px] right-[15%] eunoia-glow-secondary eunoia-float"></div>

        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="pt-32 pb-24 text-center">
            
            <h1 className="eunoia-title-large mb-8 max-w-4xl mx-auto">
              {user
                ? "EUNOIA — ваш дом для практики и встреч"
                : "EUNOIA — пространство, где свет и практика встречаются"}
            </h1>
            
            <p className="eunoia-body-large mb-12 max-w-2xl mx-auto">
              {user
                ? "Вы уже часть сообщества. Найдите событие, познакомьтесь с организаторами или поделитесь своей практикой."
                : "Сообщество для тех, кто чувствует, ищет и создаёт из внутреннего равновесия. Практики, встречи и вдохновение — онлайн и в городах Европы."}
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
              {user ? (
                <>
                  <Link to="/events" className="eunoia-btn-primary" data-testid="go-to-events-btn">
                    Перейти к событиям
                  </Link>
                  <Link to="/organizers" className="eunoia-btn-secondary" data-testid="explore-community-btn">
                    Исследовать сообщество
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="eunoia-btn-primary" data-testid="join-community-btn">
                    Присоединиться к сообществу
                  </Link>
                  <Link to="/events" className="eunoia-btn-secondary">
                    Смотреть события
                  </Link>
                </>
              )}
            </div>

            <div className="eunoia-quote max-w-lg mx-auto eunoia-body">
              «Тишина внутри — это не пустота, а источник света.»
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Breathing Space */}
      <section className="eunoia-space-xl">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-16 text-center">
            <h2 className="eunoia-title-medium mb-6">О пространстве</h2>
            <p className="eunoia-body-large max-w-3xl mx-auto">
              EUNOIA — пространство для тех, кто ищет практику и тишину. Медитации, звуковые практики, женские и мужские круги, ретриты — регулярно, онлайн и офлайн, в городах Европы.
            </p>
          </div>

          <div className="eunoia-surface p-12">
            <p className="eunoia-body max-w-3xl mx-auto text-center">
              Мы не обещаем трансформацию за один вечер. Только честное присутствие, внимательные проводники и форма, за которой есть смысл.
            </p>
          </div>
        </div>
      </section>

      {/* Practices & Events - Living Rhythm */}
      <section className="eunoia-space-xl">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-16 text-center">
            <h2 className="eunoia-title-medium mb-6">
              Практики и события
            </h2>
            <p className="eunoia-body-large max-w-2xl mx-auto">
              Онлайн и оффлайн-встречи, мягкие форматы, бережные проводники.
            </p>
          </div>

          {upcomingPreview.length > 0 && (
            <div className="eunoia-surface p-10 mb-16" data-testid="upcoming-preview">
              <h3 className="eunoia-title-small mb-3 text-center">Ближайшие события</h3>
              <p className="eunoia-body text-center mb-10 max-w-lg mx-auto">
                Небольшой анонс того, что происходит в пространстве EUNOIA.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                {upcomingPreview.map((ev) => {
                  const isOnline = (ev.type || '').toLowerCase() === 'online';
                  const placeLabel = isOnline ? 'онлайн' : (ev.city || ev.location || '');
                  let dateLabel = '';
                  try {
                    const d = new Date(ev.date);
                    const weekday = d.toLocaleDateString('ru-RU', { weekday: 'short' });
                    const dm = d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
                    dateLabel = `${weekday}, ${dm}`;
                  } catch (_e) { dateLabel = ev.date || ''; }
                  return (
                    <Link
                      key={ev.id}
                      to={`/events/${ev.id}`}
                      className="eunoia-surface-soft p-8 text-center transition-[transform,box-shadow] duration-300 ease-out hover:scale-[1.02] hover:shadow-md"
                      data-testid={`upcoming-card-${ev.id}`}
                    >
                      <div className="eunoia-caption mb-2">
                        {dateLabel}{ev.time ? `, ${ev.time}` : ''}{placeLabel ? ` (${placeLabel})` : ''}
                      </div>
                      <div className="eunoia-title-small mb-3" style={{ color: 'var(--eunoia-deep)' }}>{ev.title}</div>
                      <div className="eunoia-body line-clamp-2">{ev.description}</div>
                    </Link>
                  );
                })}
              </div>

              <div className="text-center">
                <Link to="/events" className="eunoia-btn-secondary">
                  Расписание и детали
                </Link>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="eunoia-surface-soft p-8 text-center">
              <div className="eunoia-title-small mb-4">Медитации</div>
              <div className="eunoia-body">Погружение в тишину, дыхание, наблюдение. Регулярные практики.</div>
            </div>
            <div className="eunoia-surface-soft p-8 text-center">
              <div className="eunoia-title-small mb-4">Звуковые путешествия</div>
              <div className="eunoia-body">Тибетские чаши, голос, вибрации. Тёплое расслабление и глубина.</div>
            </div>
            <div className="eunoia-surface-soft p-8 text-center">
              <div className="eunoia-title-small mb-4">Женские и мужские круги</div>
              <div className="eunoia-body">Присутствие, отклик, поддержка. Диалог в бережной форме.</div>
            </div>
            <div className="eunoia-surface-soft p-8 text-center">
              <div className="eunoia-title-small mb-4">Ретриты и встречи</div>
              <div className="eunoia-body">Дни тишины, природа, эстетика. Трансформационные пространства.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Section — only for guests */}
      {!user && (
        <section className="eunoia-space-xl">
          <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="eunoia-surface p-12">
              <div className="text-center mb-12">
                <h2 className="eunoia-title-medium mb-6">Присоединиться к EUNOIA</h2>
                <p className="eunoia-body-large max-w-xl mx-auto">
                  Вход — через email, без пароля. Мы пришлём вам безопасную ссылку.
                </p>
              </div>

              <div className="flex justify-center mb-12">
                <Link
                  to="/login"
                  className="eunoia-btn-primary"
                  data-testid="home-join-cta"
                >
                  Войти по email
                </Link>
              </div>

              <div className="eunoia-surface-soft p-8 max-w-lg mx-auto text-center">
                <div className="eunoia-quote eunoia-body mb-4">
                  «Вход — через сердце. Технологии — лишь проводники.»
                </div>
                <p className="eunoia-caption">
                  Регистрация не нужна — просто введите email на следующем шаге.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer - Sacred Ground */}
      <footer className="eunoia-space-lg">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="eunoia-divider mb-8"></div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="eunoia-body">© 2025{new Date().getFullYear() > 2025 ? `–${new Date().getFullYear()}` : ""} EUNOIA · {t("footer.tagline")}</div>
            <div className="flex flex-wrap gap-3 items-center justify-center">
              {/* Real channels appear here only when they exist. Until then we
                  surface them as calm, unmistakably-disabled pills so visitors
                  don't tap a dead href="#" link. */}
              <span
                aria-disabled="true"
                className="text-sm px-4 py-2 rounded-full cursor-default"
                style={{
                  backgroundColor: "var(--eunoia-whisper)",
                  color: "var(--eunoia-soft)",
                }}
                data-testid="footer-instagram-soon"
              >
                Instagram · скоро
              </span>
              <a
                href="https://t.me/eunoiaspace_eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm px-4 py-2 rounded-full cursor-pointer transition-[transform,box-shadow] duration-300 ease-out hover:scale-[1.02] hover:shadow-md"
                style={{
                  backgroundColor: "var(--eunoia-whisper)",
                  color: "var(--eunoia-deep)",
                  textDecoration: "none",
                }}
                data-testid="footer-telegram-link"
              >
                Telegram
              </a>
              <a
                href="mailto:eunoia.community.eu@gmail.com"
                className="eunoia-btn-primary text-sm eunoia-btn-glow"
                data-testid="footer-contact-link"
              >
                Контакты
              </a>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center md:justify-start mt-6">
            <Link to={localePath("/terms")} className="eunoia-body text-sm hover:underline" data-testid="footer-terms-link">{t("footer.terms")}</Link>
            <Link to={localePath("/privacy")} className="eunoia-body text-sm hover:underline" data-testid="footer-privacy-link">{t("footer.privacy")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;